import type { QueryClient } from '@tanstack/react-query';
import { HeadContent, Outlet, createRootRouteWithContext } from '@tanstack/react-router';
import { RouterProvider, type UNSTABLE_ToastQueue as ToastQueue } from 'react-aria-components';

import { NotFoundState } from '@/components/not-found-state.component';
import { useApplyTheme } from '@/hooks/use-apply-theme.hook';
import { useSWUpdatePrompt } from '@/hooks/use-sw-update-prompt.hook';
import {
  Icon,
  IconButton,
  Snackbar,
  Text,
  TextButton,
  Toast,
  TOAST_SLOTS,
  ToastRegion,
  type SnackbarProps,
  type TextButtonProps,
} from '@/libs/serenity-ui';
import { TanstackDevtools } from '@/libs/tanstack-devtools';

interface RouterContext {
  queryClient: QueryClient;
  toastQueue: ToastQueue<{ message: string; actionProps?: TextButtonProps; variant: SnackbarProps['variant']; }>;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: function RouteComponent() {
    const { toastQueue } = Route.useRouteContext();
    const navigate = Route.useNavigate();

    useApplyTheme();
    useSWUpdatePrompt();

    return (
      <>
        <ToastRegion queue={toastQueue}>
          {({ toast }) =>
            <Toast toast={toast}>
              <Snackbar variant={toast.content.variant}>
                <Icon />
                <Text>{toast.content.message}</Text>
                {toast.content.actionProps && <TextButton {...toast.content.actionProps} />}
                <IconButton slot={TOAST_SLOTS.CLOSE} />
              </Snackbar>
            </Toast>}
        </ToastRegion>
        <HeadContent />
        <RouterProvider navigate={(path, options) => navigate({ to: path, ...(options ?? {}) })}>
          <Outlet />
        </RouterProvider>
        <TanstackDevtools />
      </>
    );
  },
  notFoundComponent: (props) => <NotFoundState withHeader {...props} />,
});
